import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { MatPaginator, MatSort, PageEvent, Sort } from '@angular/material';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, finalize, takeUntil } from 'rxjs/operators';
import { BackendResponse, HttpService } from 'vas-mobile-api';

import { Operator } from './chips-and-fields';
import { Delivery } from './delivery';

export class DeliveriesDataSource implements DataSource<Delivery> {


    private deliveriesSubject = new BehaviorSubject<Delivery[]>([]);
    private loadingSubject = new BehaviorSubject<boolean>(false);
    private currentFilterSubject = new Subject<Filter[]>();
    private _paginator: MatPaginator;
    private _sort: MatSort;
    // original, untouched data, used to generate sortedArray
    private dataArray: Delivery[];
    private sortedArray: Delivery[];
    private fields: string[];
    private currentFilters: Filter[] = [];
    private currentSortation: Sort;
    private _isMobile: boolean = null;

    private ngUnsubscribe = new Subject();
    private sortReset = new Subject();
    private paginatorReset = new Subject();
    private abortOngoingRequest = new Subject();

    public loading$ = this.loadingSubject.asObservable();

    constructor(private httpService: HttpService, fields: string[], filters?: Filter[]) {
        this.fields = fields;
        if (filters) {
            this.currentFilters = filters;
        }

        this.loadDeliveries();

        this.currentFilterSubject.pipe(takeUntil(this.ngUnsubscribe), debounceTime(300)).subscribe((newFilters) => {
            this.currentFilters = newFilters;
            this.loadDeliveries();
        });
    }

    public set isMobile(isMobile: boolean) {
        if (isMobile !== this._isMobile) {
            if (this._isMobile === null) {
                this._isMobile = isMobile;
            } else {
                this._isMobile = isMobile;
                // do a reload to update Sort / Pagination / Scrolling
                this.loadDeliveries();
            }
        }
    }

    public get dataSize() {
        return this.dataArray ? this.dataArray.length : -1;
    }

    /**
     * Reapplies the filters (chips) for the view, causing a backend request.
     * New Filters are applied after a 300ms delay between inputs
     *
     * @param {Filter[]} filters
     * @memberof DeliveriesDataSource
     */
    public setFilters(filters: Filter[]) {
        if (!this.filtersEqual(filters, this.currentFilters)) {
            this.currentFilterSubject.next(filters);
            // this.currentFilters = filters;
            // this.loadDeliveries();
        }
    }

    /**
     * Subscribes to page changes and limits the view accordingly
     *
     * @memberof DeliveriesDataSource
     */
    public set paginator(paginator: MatPaginator) {
        this._paginator = paginator;
        this.paginatorReset.next();
        this._paginator.page.pipe(takeUntil(this.paginatorReset)).subscribe((pageEvent: PageEvent) => {
            const dataSelection = this.doPaginate(this.sortedArray, pageEvent.pageIndex, pageEvent.pageSize);
            this.deliveriesSubject.next(dataSelection);
        });
    }

    private doPaginate(data: Delivery[], pageIndex: number, pageSize: number) {
        return data.slice(pageIndex * pageSize,
            (pageIndex + 1) * pageSize);
    }

    /**
     * Subscribes to sortChange and does client side sorting if totalElements < 1000, else server side sorting
     *
     * @memberof DeliveriesDataSource
     */
    public set sort(sort: MatSort) {
        this._sort = sort;
        this.sortReset.next();
        this._sort.sortChange.pipe(takeUntil(this.sortReset)).subscribe((sortEvent: Sort) => {
            this.currentSortation = sortEvent;
            if (this.dataArray && this.dataArray.length >= 1000) {
                // limit reached, make a new backend request with the sorting
                this.loadDeliveries();
            } else {
                // limit not reached, do client side sorting
                this.sortedArray = this.doSort(this.dataArray);
                if (this._paginator) {
                    this._paginator.pageIndex = 0;
                    const dataSelection = this.doPaginate(this.sortedArray, this._paginator.pageIndex, this._paginator.pageSize);
                    this.deliveriesSubject.next(dataSelection);
                } else {
                    this.deliveriesSubject.next(this.sortedArray);
                }
            }
        });
    }

    private doSort(data: Delivery[]) {
        if (this.currentSortation && this.currentSortation.direction) {
            // create a copy so that the original is untouched
            return data.slice().sort((a, b) => {
                if (a[this.currentSortation.active] < b[this.currentSortation.active]) {
                    return this.currentSortation.direction === 'asc' ? -1 : 1;
                } else if (a[this.currentSortation.active] > b[this.currentSortation.active]) {
                    return this.currentSortation.direction === 'asc' ? 1 : -1;
                } else {
                    return 0;
                }
            });
        } else {
            return data;
        }
    }

    connect(collectionViewer: CollectionViewer): Observable<Delivery[]> {
        return this.deliveriesSubject.asObservable();
    }
    disconnect(collectionViewer: CollectionViewer): void {
        this.deliveriesSubject.complete();
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
        this.paginatorReset.next();
        this.paginatorReset.complete();
        this.sortReset.next();
        this.sortReset.complete();
        this.abortOngoingRequest.complete();
    }

    private loadDeliveries() {
        // if (this.loadingSubject.value) {
        //     return;
        // }
        this.abortOngoingRequest.next();
        this.loadingSubject.next(true);
        this.deliveriesSubject.next(null);
        this.dataArray = null;

        let orderBy = [''];
        if (this.currentSortation && this.currentSortation.direction) {
            orderBy = [this.currentSortation.active + ' ' + this.currentSortation.direction];
        }

        this.httpService.post<BackendResponse<Delivery[]>>({
            pathId: 'listFilteredDeliveriesWithLimit', payload: this.currentFilters, urlParams: {
                limit: 1000,
                orderBy: orderBy,
                fields: this.fields
            }
        })
            .pipe(
                takeUntil(this.abortOngoingRequest),
                catchError(() => of({ data: [] })),
                finalize(() => this.loadingSubject.next(false)))
            .subscribe((response) => {
                // this.loadingSubject.next(false);
                this.dataArray = response.data;
                // make a copy
                this.sortedArray = this.dataArray.slice();
                if (this._isMobile) {
                    this.deliveriesSubject.next(this.sortedArray);
                } else {
                    if (this._sort) {
                        this.sortedArray = this.doSort(this.dataArray);
                    }
                    if (this._paginator) {
                        this._paginator.length = this.dataArray.length;
                        this._paginator.pageIndex = 0;
                        const dataSelection = this.doPaginate(this.sortedArray,
                            this._paginator.pageIndex, this._paginator.pageSize);
                        this.deliveriesSubject.next(dataSelection);
                    } else {
                        this.deliveriesSubject.next(this.sortedArray);
                    }
                }
            });
    }

    private filtersEqual(filterA: Filter[], filterB: Filter[]): boolean {
        if (!filterA && !filterB) {
            return true;
        } else if (!filterA && filterB) {
            return false;
        } else if (filterA && !filterB) {
            return false;
        } else if (filterA.length !== filterB.length) {
            return false;
        } else {
            const sortedCopyA = filterA.slice().sort(this.sortFilter);
            const sortedCopyB = filterB.slice().sort(this.sortFilter);

            for (let i = 0; i < sortedCopyA.length; i++) {
                if (sortedCopyA[i].key !== sortedCopyB[i].key || sortedCopyA[i].value !== sortedCopyB[i].value) {
                    return false;
                }
            }
            return true;
        }
    }

    private sortFilter(a: Filter, b: Filter) {
        if (a.key < b.key) {
            return -1;
        } else if (a.key > b.key) {
            return 1;
        } else {
            return 0;
        }
    }

    public refresh() {
        this.loadDeliveries();
    }
}

export interface Filter {
    key: string;
    value: string;
    operator: Operator;
}
